import {
  Add as AddIcon,
  CheckCircleOutline,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ErrorOutline,
  ExpandLess,
  ExpandMore,
  MoreVert,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  Switch,
  Tab,
  TablePagination,
  Tabs,
  Typography,
} from "@mui/material";
import axios from "axios";
import moment from "moment-timezone";
import React, { useCallback, useEffect, useState } from "react";
import {
  DAYS_OF_WEEK,
  SCHEDULED_TASK_EXECUTION_STATUS_COMPLETED,
  SCHEDULED_TASK_EXECUTION_STATUS_FAILED,
  SCHEDULED_TASK_EXECUTION_STATUS_RUNNING,
  SCHEDULED_TASK_FREQUENCY_DAILY,
  SCHEDULED_TASK_FREQUENCY_MONTHLY,
  SCHEDULED_TASK_FREQUENCY_WEEKLY,
  SCHEDULED_TASK_STATUS_ACTIVE,
  SCHEDULED_TASK_STATUS_COMPLETED,
  SCHEDULED_TASK_STATUS_PAUSED,
  SCHEDULED_TASK_TYPE_ONE_TIME,
  SCHEDULED_TASK_TYPE_RECURRING,
} from "../../../constants";
import { Employee, MessageBlock, ToolBlock } from "../../../types";
import { hostname } from "../../../utils";
import { StructuredMessageContent } from "../../../components/StructuredChatMessage";
import ScheduledTaskFormDialog from "./ScheduledTaskFormDialog";

interface ScheduledTasksDialogProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  companyName: string;
  getToken: () => Promise<string>;
  employees: Array<Employee>;
  currentPlan: any;
}

interface ScheduledTask {
  id: string;
  name: string;
  employeeId: string;
  prompt: string;
  scheduleType: string;
  scheduledAt?: string | null;
  recurringConfig?: {
    frequency: string;
    time: string;
    daysOfWeek?: number[];
    dayOfMonth?: number;
  } | null;
  contextSettings?: any;
  timezone: string;
  nextRunAt?: string | null;
  lastRunAt?: string | null;
  status: string;
  runCount: number;
  createdDate: string;
  employee?: {
    id: string;
    name: string;
    picture?: string;
    title?: string;
  };
}

interface ScheduledTaskExecution {
  id: string;
  scheduledTaskId: string;
  status: string;
  scheduledTime: string;
  startedAt?: string | null;
  completedAt?: string | null;
  durationMs?: number | null;
  promptSent: string;
  response?: string | null;
  toolCalls?: any[] | null;
  errorMessage?: string | null;
  tokensUsed?: number | null;
  createdDate: string;
  employee?: {
    id: string;
    name: string;
    picture?: string;
    title?: string;
  };
  task?: {
    id: string;
    name: string;
  };
}

// Convert execution toolCalls and response into MessageBlock format for structured rendering
const convertExecutionToBlocks = (execution: ScheduledTaskExecution): MessageBlock[] => {
  const blocks: MessageBlock[] = [];

  // Add tool calls as tool blocks
  if (execution.toolCalls && Array.isArray(execution.toolCalls)) {
    for (const toolCall of execution.toolCalls) {
      // Tool name can be in 'type', 'name', or 'tool' field depending on how it was stored
      const toolName = toolCall.type || toolCall.name || toolCall.tool || "unknown";
      const toolBlock: ToolBlock = {
        type: "tool",
        name: toolName,
        args: toolCall.args || toolCall.arguments || toolCall.input,
        status: "completed",
        output: typeof toolCall.content === "string"
          ? toolCall.content
          : typeof toolCall.output === "string"
            ? toolCall.output
            : toolCall.content ? JSON.stringify(toolCall.content, null, 2)
              : toolCall.output ? JSON.stringify(toolCall.output, null, 2)
                : undefined,
        displayName: toolCall.displayName,
        logo: toolCall.logo,
      };
      blocks.push(toolBlock);
    }
  }

  // Add response text as a text block
  if (execution.response) {
    blocks.push({
      type: "text",
      content: execution.response,
    });
  }

  return blocks;
};

export default function ScheduledTasksDialog({
  open,
  onClose,
  companyId,
  companyName,
  getToken,
  employees,
  currentPlan,
}: ScheduledTasksDialogProps) {
  const [currentTab, setCurrentTab] = useState(0);
  const [tasks, setTasks] = useState<ScheduledTask[]>([]);
  const [executions, setExecutions] = useState<ScheduledTaskExecution[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [loadingExecutions, setLoadingExecutions] = useState(false);
  const [tasksPage, setTasksPage] = useState(0);
  const [tasksPageSize, setTasksPageSize] = useState(10);
  const [tasksTotal, setTasksTotal] = useState(0);
  const [executionsPage, setExecutionsPage] = useState(0);
  const [executionsPageSize, setExecutionsPageSize] = useState(10);
  const [executionsTotal, setExecutionsTotal] = useState(0);
  const [showCompleted, setShowCompleted] = useState(false);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<ScheduledTask | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [expandedExecutionId, setExpandedExecutionId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });
  const [deleteConfirmTaskId, setDeleteConfirmTaskId] = useState<string | null>(null);

  const fetchTasks = useCallback(async () => {
    if (!open || !companyId) return;

    setLoadingTasks(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams({
        companyId,
        page: (tasksPage + 1).toString(),
        pageSize: tasksPageSize.toString(),
        includeCompleted: showCompleted.toString(),
      });

      const response = await axios.get(`${hostname}/scheduled-tasks?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setTasks(response.data.tasks || []);
      setTasksTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching scheduled tasks:", error);
      setSnackbar({
        open: true,
        message: "Failed to load scheduled tasks",
        severity: "error",
      });
    } finally {
      setLoadingTasks(false);
    }
  }, [open, companyId, getToken, tasksPage, tasksPageSize, showCompleted]);

  const fetchExecutions = useCallback(async () => {
    if (!open || !companyId) return;

    setLoadingExecutions(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams({
        companyId,
        page: (executionsPage + 1).toString(),
        pageSize: executionsPageSize.toString(),
      });

      const response = await axios.get(`${hostname}/scheduled-task-executions?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setExecutions(response.data.executions || []);
      setExecutionsTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching executions:", error);
      setSnackbar({
        open: true,
        message: "Failed to load execution history",
        severity: "error",
      });
    } finally {
      setLoadingExecutions(false);
    }
  }, [open, companyId, getToken, executionsPage, executionsPageSize]);

  useEffect(() => {
    if (currentTab === 0) {
      fetchTasks();
    } else {
      fetchExecutions();
    }
  }, [currentTab, fetchTasks, fetchExecutions]);

  const handleCreateTask = async (taskData: any) => {
    const token = await getToken();
    await axios.post(
      `${hostname}/scheduled-tasks`,
      { ...taskData, companyId },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setSnackbar({ open: true, message: "Task created", severity: "success" });
    fetchTasks();
  };

  const handleUpdateTask = async (taskData: any) => {
    if (!editingTask) return;

    const token = await getToken();
    await axios.put(
      `${hostname}/scheduled-tasks/${editingTask.id}`,
      { ...taskData, companyId },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setSnackbar({ open: true, message: "Task updated", severity: "success" });
    setEditingTask(null);
    fetchTasks();
  };

  const handleDeleteTask = async (taskId: string) => {
    try {
      const token = await getToken();
      await axios.delete(`${hostname}/scheduled-tasks/${taskId}?companyId=${companyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSnackbar({ open: true, message: "Task deleted", severity: "success" });
      fetchTasks();
    } catch (error) {
      console.error("Error deleting task:", error);
      setSnackbar({ open: true, message: "Failed to delete task", severity: "error" });
    } finally {
      setDeleteConfirmTaskId(null);
    }
  };

  const handlePauseTask = async (taskId: string) => {
    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/scheduled-tasks/${taskId}/pause`,
        { companyId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSnackbar({ open: true, message: "Task paused", severity: "success" });
      fetchTasks();
    } catch (error) {
      console.error("Error pausing task:", error);
      setSnackbar({ open: true, message: "Failed to pause task", severity: "error" });
    }
  };

  const handleResumeTask = async (taskId: string) => {
    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/scheduled-tasks/${taskId}/resume`,
        { companyId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSnackbar({ open: true, message: "Task resumed", severity: "success" });
      fetchTasks();
    } catch (error) {
      console.error("Error resuming task:", error);
      setSnackbar({ open: true, message: "Failed to resume task", severity: "error" });
    }
  };

  const getScheduleSummary = (task: ScheduledTask) => {
    if (task.scheduleType === SCHEDULED_TASK_TYPE_ONE_TIME) {
      if (task.scheduledAt) {
        return `Once on ${moment(task.scheduledAt).tz(task.timezone).format("MMM D, YYYY h:mm A")}`;
      }
      return "One-time";
    }

    if (task.recurringConfig) {
      const time = task.recurringConfig.time || "09:00";
      const timeFormatted = moment(time, "HH:mm").format("h:mm A");

      if (task.recurringConfig.frequency === SCHEDULED_TASK_FREQUENCY_DAILY) {
        return `Daily at ${timeFormatted}`;
      } else if (task.recurringConfig.frequency === SCHEDULED_TASK_FREQUENCY_WEEKLY) {
        const days = task.recurringConfig.daysOfWeek || [];
        const dayNames = days
          .map((d) => DAYS_OF_WEEK.find((day) => day.value === d)?.label?.substring(0, 3))
          .filter(Boolean)
          .join(", ");
        return `${dayNames} at ${timeFormatted}`;
      } else if (task.recurringConfig.frequency === SCHEDULED_TASK_FREQUENCY_MONTHLY) {
        const day = task.recurringConfig.dayOfMonth || 1;
        return `${day}${day === 1 ? "st" : day === 2 ? "nd" : day === 3 ? "rd" : "th"} of month at ${timeFormatted}`;
      }
    }

    return "Recurring";
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case SCHEDULED_TASK_STATUS_ACTIVE:
        return <Chip label="Active" color="primary" size="small" />;
      case SCHEDULED_TASK_STATUS_PAUSED:
        return <Chip label="Paused" color="warning" size="small" />;
      case SCHEDULED_TASK_STATUS_COMPLETED:
        return <Chip label="Completed" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const getExecutionStatusChip = (status: string) => {
    switch (status) {
      case SCHEDULED_TASK_EXECUTION_STATUS_COMPLETED:
        return <Chip label="Completed" color="primary" size="small" icon={<CheckCircleOutline />} />;
      case SCHEDULED_TASK_EXECUTION_STATUS_FAILED:
        return <Chip label="Failed" color="error" size="small" icon={<ErrorOutline />} />;
      case SCHEDULED_TASK_EXECUTION_STATUS_RUNNING:
        return <Chip label="Running" color="warning" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const selectedTask = tasks.find((t) => t.id === selectedTaskId);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ScheduleIcon />
              <Typography variant="h6">Scheduled Tasks</Typography>
              <Chip
                label="Beta"
                size="small"
                sx={{
                  backgroundColor: "grey.500",
                  color: "white",
                  fontSize: "0.7rem",
                  height: 20,
                }}
              />
            </Box>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0, minHeight: 500, height: 500 }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
            <Tabs
              value={currentTab}
              onChange={(_, v) => setCurrentTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              <Tab label="Scheduled" />
              <Tab label="History" />
            </Tabs>
          </Box>

          {/* Scheduled Tab */}
          {currentTab === 0 && (
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={showCompleted}
                      onChange={(e) => setShowCompleted(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Show completed"
                />
                <Box sx={{ display: "flex", gap: 1 }}>
                  <IconButton onClick={() => fetchTasks()} size="small">
                    <RefreshIcon />
                  </IconButton>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setEditingTask(null);
                      setFormDialogOpen(true);
                    }}
                    size="small"
                  >
                    New Task
                  </Button>
                </Box>
              </Box>

              {loadingTasks ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : tasks.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography color="text.secondary">
                    No scheduled tasks yet. Create one to get started.
                  </Typography>
                </Box>
              ) : (
                <>
                  <List dense>
                    {tasks.map((task) => (
                      <ListItem
                        key={task.id}
                        sx={{
                          border: 1,
                          borderColor: "divider",
                          borderRadius: 1,
                          mb: 1,
                          bgcolor: "background.paper",
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar src={task.employee?.picture}>
                            {task.employee?.name?.[0] || "?"}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography variant="subtitle2">{task.name}</Typography>
                              {getStatusChip(task.status)}
                              {task.scheduleType === SCHEDULED_TASK_TYPE_RECURRING && (
                                <Chip label="Recurring" size="small" variant="outlined" />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box component="span">
                              <Typography variant="caption" component="span" display="block">
                                {task.employee?.name} • {getScheduleSummary(task)}
                              </Typography>
                              {task.nextRunAt && task.status === SCHEDULED_TASK_STATUS_ACTIVE && (
                                <Typography variant="caption" component="span" color="primary">
                                  Next: {moment(task.nextRunAt).fromNow()}
                                </Typography>
                              )}
                              {task.runCount > 0 && (
                                <Typography variant="caption" component="span" sx={{ ml: 1 }}>
                                  • {task.runCount} run{task.runCount > 1 ? "s" : ""}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            onClick={(e) => {
                              setMenuAnchorEl(e.currentTarget);
                              setSelectedTaskId(task.id);
                            }}
                            size="small"
                          >
                            <MoreVert />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>

                  <TablePagination
                    component="div"
                    count={tasksTotal}
                    page={tasksPage}
                    onPageChange={(_, page) => setTasksPage(page)}
                    rowsPerPage={tasksPageSize}
                    onRowsPerPageChange={(e) => {
                      setTasksPageSize(parseInt(e.target.value, 10));
                      setTasksPage(0);
                    }}
                    rowsPerPageOptions={[5, 10, 25]}
                  />
                </>
              )}
            </Box>
          )}

          {/* History Tab */}
          {currentTab === 1 && (
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                <IconButton onClick={() => fetchExecutions()} size="small">
                  <RefreshIcon />
                </IconButton>
              </Box>

              {loadingExecutions ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : executions.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography color="text.secondary">
                    No execution history yet.
                  </Typography>
                </Box>
              ) : (
                <>
                  <List dense>
                    {executions.map((execution) => (
                      <React.Fragment key={execution.id}>
                        <ListItem
                          sx={{
                            border: 1,
                            borderColor: "divider",
                            borderRadius: 1,
                            mb: 1,
                            bgcolor: "background.paper",
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            setExpandedExecutionId(
                              expandedExecutionId === execution.id ? null : execution.id
                            )
                          }
                        >
                          <ListItemAvatar>
                            <Avatar src={execution.employee?.picture}>
                              {execution.employee?.name?.[0] || "?"}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Typography variant="subtitle2">
                                  {execution.task?.name || "Unknown Task"}
                                </Typography>
                                {getExecutionStatusChip(execution.status)}
                              </Box>
                            }
                            secondary={
                              <Box component="span">
                                <Typography variant="caption" component="span" display="block">
                                  {moment(execution.scheduledTime).format("MMM D, YYYY h:mm A")}
                                  {execution.durationMs && ` • ${(execution.durationMs / 1000).toFixed(1)}s`}
                                </Typography>
                              </Box>
                            }
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              size="small"
                              onClick={() =>
                                setExpandedExecutionId(
                                  expandedExecutionId === execution.id ? null : execution.id
                                )
                              }
                            >
                              {expandedExecutionId === execution.id ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>

                        <Collapse in={expandedExecutionId === execution.id}>
                          <Box
                            sx={{
                              ml: 7,
                              mr: 2,
                              mb: 2,
                              p: 2,
                              bgcolor: "action.hover",
                              borderRadius: 1,
                            }}
                          >
                            <Typography variant="subtitle2" gutterBottom>
                              Prompt:
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 2, whiteSpace: "pre-wrap" }}>
                              {execution.promptSent}
                            </Typography>

                            {(execution.toolCalls && execution.toolCalls.length > 0) || execution.response ? (
                              <>
                                <Typography variant="subtitle2" gutterBottom>
                                  Response:
                                </Typography>
                                <Box sx={{ maxHeight: 400, overflow: "auto" }}>
                                  <StructuredMessageContent
                                    blocks={convertExecutionToBlocks(execution)}
                                    messageId={execution.id}
                                  />
                                </Box>
                              </>
                            ) : null}

                            {execution.errorMessage && (
                              <Alert severity="error" sx={{ mt: 1 }}>
                                {execution.errorMessage}
                              </Alert>
                            )}

                            {execution.tokensUsed && (
                              <Typography variant="caption" color="text.secondary">
                                Tokens used: {execution.tokensUsed}
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </React.Fragment>
                    ))}
                  </List>

                  <TablePagination
                    component="div"
                    count={executionsTotal}
                    page={executionsPage}
                    onPageChange={(_, page) => setExecutionsPage(page)}
                    rowsPerPage={executionsPageSize}
                    onRowsPerPageChange={(e) => {
                      setExecutionsPageSize(parseInt(e.target.value, 10));
                      setExecutionsPage(0);
                    }}
                    rowsPerPageOptions={[5, 10, 25]}
                  />
                </>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Task Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => {
          setMenuAnchorEl(null);
          setSelectedTaskId(null);
        }}
      >
        <MenuItem
          onClick={() => {
            if (selectedTask) {
              setEditingTask(selectedTask);
              setFormDialogOpen(true);
            }
            setMenuAnchorEl(null);
            setSelectedTaskId(null);
          }}
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        {selectedTask?.status === SCHEDULED_TASK_STATUS_ACTIVE && (
          <MenuItem
            onClick={() => {
              if (selectedTaskId) handlePauseTask(selectedTaskId);
              setMenuAnchorEl(null);
              setSelectedTaskId(null);
            }}
          >
            <PauseIcon fontSize="small" sx={{ mr: 1 }} />
            Pause
          </MenuItem>
        )}
        {selectedTask?.status === SCHEDULED_TASK_STATUS_PAUSED && (
          <MenuItem
            onClick={() => {
              if (selectedTaskId) handleResumeTask(selectedTaskId);
              setMenuAnchorEl(null);
              setSelectedTaskId(null);
            }}
          >
            <PlayIcon fontSize="small" sx={{ mr: 1 }} />
            Resume
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            if (selectedTaskId) setDeleteConfirmTaskId(selectedTaskId);
            setMenuAnchorEl(null);
            setSelectedTaskId(null);
          }}
          sx={{ color: "error.main" }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Form Dialog */}
      <ScheduledTaskFormDialog
        open={formDialogOpen}
        onClose={() => {
          setFormDialogOpen(false);
          setEditingTask(null);
        }}
        onSave={editingTask ? handleUpdateTask : handleCreateTask}
        editingTask={editingTask}
        employees={employees}
        company={{ id: companyId, name: companyName }}
        getToken={getToken}
        currentPlan={currentPlan}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmTaskId !== null}
        onClose={() => setDeleteConfirmTaskId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Task</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this scheduled task?
          </Typography>
        </DialogContent>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, p: 2, pt: 0 }}>
          <Button onClick={() => setDeleteConfirmTaskId(null)}>Cancel</Button>
          <Button
            onClick={() => deleteConfirmTaskId && handleDeleteTask(deleteConfirmTaskId)}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </Box>
      </Dialog>
    </>
  );
}
